import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { SaveIcon, Plus, Trash2 } from "lucide-react";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import ConfigurationPanel from "../configuration/ConfigurationPanel";
import { Config } from "../../model_gen/config";
import { ConfigurationPanelRef } from "../../types/IApplication";
import { LLMActionConfig, ActionReference } from "../../types/AgentAction";
import PromptModal from "../ui/PromptModal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

interface AgentActionLLMEditProps {
  config: LLMActionConfig;
  onChange: (config: LLMActionConfig) => void;
  applicationId?: number;
  showSaveActions?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  isValid?: boolean;
  references?: ActionReference[];
  onReferencesChange?: (references: ActionReference[]) => void;
}

export interface AgentActionLLMEditRef {
  hasUnsavedChanges: () => boolean;
  saveUnsavedChanges: () => Promise<void>;
  getCurrentConfig: () => LLMActionConfig;
}

const AgentActionLLMEdit = forwardRef<AgentActionLLMEditRef, AgentActionLLMEditProps>(({ 
  config, 
  onChange, 
  applicationId,
  showSaveActions = false,
  onSave,
  onCancel,
  isSaving = false,
  isValid = true,
  references = [],
  onReferencesChange
}, ref) => {
  const configPanelRef = useRef<ConfigurationPanelRef>(null);
  const [hasUnsavedConfigChanges, setHasUnsavedConfigChanges] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  const [promptModalOpen, setPromptModalOpen] = useState(false);
  const [formData, setFormData] = useState<LLMActionConfig>(() => {
    // Default config
    const defaultConfig: LLMActionConfig = {
      model_config: {
        model: "gpt-4o",
        temperature: 0.7,
        max_tokens: null,
        inference_location: 1, // Server
        evaluation_enabled: false,
        apply_corrections: false,
        safe_mode: false,
        streaming: false,
        logprobs: false,
      },
      prompt: "",
      templateVariables: {},
    };
    
    // Merge with provided config, ensuring model_config is properly merged
    const mergedConfig = {
      ...defaultConfig,
      ...config,
      model_config: {
        ...defaultConfig.model_config,
        ...(config.model_config || {}),
      },
    };
    
    return mergedConfig;
  });

  useEffect(() => {
    onChange(formData);
  }, [formData, onChange]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    hasUnsavedChanges: () => hasUnsavedConfigChanges,
    getCurrentConfig: () => formData,
    saveUnsavedChanges: async () => {
      return new Promise<void>((resolve) => {
        if (hasUnsavedConfigChanges && configPanelRef.current) {
          setPendingSave(true);
          configPanelRef.current.saveChanges();
          
          let attempts = 0;
          const maxAttempts = 40; // 2 seconds max wait
          
          const checkSaveComplete = () => {
            attempts++;
            
            if (!hasUnsavedConfigChanges) {
              resolve();
            } else if (attempts >= maxAttempts) {
              resolve();
            } else {
              setTimeout(checkSaveComplete, 50);
            }
          };
          
          setTimeout(checkSaveComplete, 100);
        } else {
          resolve();
        }
      });
    }
  }), [hasUnsavedConfigChanges, onSave, formData]);

  // Handle pending save after config panel updates
  useEffect(() => {
    if (pendingSave && !hasUnsavedConfigChanges) {
      // Config has been saved, now trigger the parent save
      setPendingSave(false);
      onSave?.();
    }
  }, [pendingSave, hasUnsavedConfigChanges, onSave]);

  const handleConfigUpdate = (
    newConfig: Partial<Config>,
    loadingHandler?: (loading: boolean) => void | null,
    save?: boolean
  ) => {
    // When save is true, the ConfigurationPanel is committing its unsaved changes
    // Update our formData with the new configuration
    if (save) {
      setFormData(prev => {
        const updated = {
          ...prev,
          model_config: {
            ...prev.model_config,
            ...newConfig,
          },
        };
        return updated;
      });
    }

    // Handle loading state if provided
    if (loadingHandler) {
      // Simulate completion of the update operation
      loadingHandler(false);
    }
  };

  const handlePromptChange = (prompt: string) => {
    setFormData(prev => ({
      ...prev,
      prompt
    }));
  };

  const handlePromptSave = (prompt: string) => {
    handlePromptChange(prompt);
    setPromptModalOpen(false);
  };

  const handleAddReference = () => {
    const newReference: ActionReference = {
      key_name: "",
      description: "",
      is_required: false,
    };
    onReferencesChange?.([...references, newReference]);
  };

  const handleRemoveReference = (index: number) => {
    const updated = references.filter((_, i) => i !== index);
    onReferencesChange?.(updated);
  };

  const handleReferenceChange = (index: number, field: keyof ActionReference, value: string | boolean) => {
    const updated = references.map((ref, i) => 
      i === index ? { ...ref, [field]: value } : ref
    );
    onReferencesChange?.(updated);
  };

  return (
    <Card className="border-border">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl font-gc-med text-foreground">Model Configuration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Model Configuration Section */}
        <ConfigurationPanel
          ref={configPanelRef}
          config={formData.model_config}
          updateConfig={handleConfigUpdate}
          resetConfig={() => {}}
          readOnly={false}
          allowSecondaryModel={true}
          intentGroupId={applicationId}
          isModalOpen={true}  // Hide save/reset buttons since we have our own
          onSaveStateChange={setHasUnsavedConfigChanges}
          disableClientInference={true}
        />

        {/* Divider */}
        <div className="border-t border-border" />

        {/* Prompt Section */}
        <div className="space-y-3">
          <h3 className="text-xl font-gc-med text-foreground font-semibold">
            Prompt <span className="text-red-500">*</span>
          </h3>
          <Button
            onClick={() => setPromptModalOpen(true)}
            variant="outline"
            className="w-full h-24 justify-start items-start text-left p-4 font-normal"
          >
            <div className="w-full">
              {formData.prompt ? (
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">
                    {formData.prompt.length} characters • {formData.prompt.split("\n").length} lines
                  </div>
                  <div className="text-sm line-clamp-2 text-foreground font-mono">
                    {formData.prompt}
                  </div>
                </div>
              ) : (
                <div className="text-muted-foreground">
                  Click to add prompt template...
                </div>
              )}
            </div>
          </Button>
          <p className="text-xs text-muted-foreground">
            Use double curly braces to insert dynamic values from template variables.
            Example: {"{{ user_input }}"} or {"{{ agent.name }}"}
          </p>
        </div>

        {/* Divider */}
        <div className="border-t border-border" />

        {/* References Section */}
        <div className="space-y-3">
          <h3 className="text-xl font-gc-med text-foreground font-semibold">
            References <span className="text-muted-foreground font-normal text-sm">(optional)</span>
          </h3>
          
          {references.length === 0 ? (
            <div className="flex items-center justify-center py-8 border border-dashed border-border rounded-lg">
              <Button
                onClick={handleAddReference}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Reference
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[200px]">Reference Key Name</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-[120px]">Required</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {references.map((reference, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Input
                          value={reference.key_name}
                          onChange={(e) => handleReferenceChange(index, "key_name", e.target.value)}
                          placeholder="e.g., user_summary"
                          className="font-mono text-sm"
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          value={reference.description}
                          onChange={(e) => handleReferenceChange(index, "description", e.target.value)}
                          placeholder="Description of what this reference contains"
                          className="text-sm"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={reference.is_required}
                            onCheckedChange={(checked) => handleReferenceChange(index, "is_required", checked)}
                          />
                          <Label className="text-sm">Required</Label>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => handleRemoveReference(index)}
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button
                onClick={handleAddReference}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Reference
              </Button>
            </div>
          )}
        </div>

        {/* Save Actions Section */}
        {showSaveActions && (
          <>
            <div className="border-t border-border" />
            <div className="flex items-center justify-between pt-6">
              <div>
                <p className="text-sm text-muted-foreground">
                  Fields marked with <span className="text-red-500">*</span> are required
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={onCancel} disabled={isSaving}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => {
                    // If there are unsaved config changes, save them first
                    if (hasUnsavedConfigChanges && configPanelRef.current) {
                      setPendingSave(true);
                      configPanelRef.current.saveChanges();
                    } else {
                      onSave?.();
                    }
                  }}
                  disabled={!isValid || isSaving || pendingSave}
                  className="gap-2"
                >
                  <SaveIcon className="w-4 h-4" />
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>

      {/* Prompt Modal */}
      <PromptModal
        isOpen={promptModalOpen}
        onClose={() => setPromptModalOpen(false)}
        onSave={handlePromptSave}
        title="Prompt Template"
        description="Enter your LLM prompt template. Use {{variable_name}} for dynamic values."
        initialValue={formData.prompt || ""}
        placeholder="Enter your prompt template here. Use {{variable_name}} for dynamic values."
      />
    </Card>
  );
});

AgentActionLLMEdit.displayName = "AgentActionLLMEdit";

export default AgentActionLLMEdit; 